/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Router} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '@environments/environment';

/** Đường dẫn redirect khi đổi version trên trang hội thoại */
const CONVERSATION_PATHS: Record<string, string> = {
    v1: '/app/conversation',
    v2: '/conversation',
};

/** Chỉ viewport điện thoại/tablet dọc nhỏ dùng UI app riêng. */
const APP_LAYOUT_MAX_WIDTH = 992;

export interface IDeviceLayoutInfo {
    width: number;
    /** Thiết bị nhập chính là cảm ứng (matchMedia pointer: coarse) */
    coarsePointer: boolean;
    userAgent: string;
    /** Đang chạy trong app mobile (app native ghi cờ isMobileApp vào localStorage) */
    isMobileApp: boolean;
}

/**
 * Thiết bị nên dùng shell app thay bản web V2: đang trong app mobile, hoặc màn < 992px trên thiết bị cầm tay
 * (cảm ứng / User-Agent điện thoại). Màn nhỏ trên máy tính (thu nhỏ cửa sổ) vẫn giữ bản web.
 */
export function shouldUseAppLayout(device: IDeviceLayoutInfo): boolean {
    if (device.isMobileApp) {
        return true;
    }
    const isHandheld = device.coarsePointer || /Mobi|iPhone|iPod|Android/i.test(device.userAgent);
    return device.width < APP_LAYOUT_MAX_WIDTH && isHandheld;
}

@Injectable({providedIn: 'root'})
export class LayoutUIService {
    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private router: Router,
    ) {
    }

    /** Đọc phiên bản giao diện hiện tại từ URL — mặc định v2 */
    getVersion(): 'v1' | 'v2' {
        const val = this.router.parseUrl(this.router.url).queryParams['layoutUI'];
        return val === 'v1' ? 'v1' : 'v2';
    }

    isV2(): boolean {
        return this.getVersion() === 'v2';
    }

    /**
     * BE mặc định trả bản web V2 khi chưa có cookie layoutUI (không biết màn hình). Bản web chạy trên điện thoại /
     * app mobile → chuyển trang sang cùng URL kèm layoutUI=app để PHP trả shell app và ghi nhớ cookie.
     * Trả về true nếu đang chuyển trang (app không cần khởi tạo tiếp).
     */
    ensureDeviceLayout(): boolean {
        if (!isPlatformBrowser(this.platformId) || environment.isMobileLayout || environment.isAdsModule) {
            return false;
        }

        const url = new URL(window.location.href);
        // Đã yêu cầu app mà PHP vẫn trả bản web (VD: môi trường chưa có build app) → không chuyển lặp
        if (url.searchParams.get('layoutUI') === 'app') {
            return false;
        }

        let isMobileApp = false;
        try {
            isMobileApp = !!localStorage.getItem('isMobileApp');
        } catch { /* Trình duyệt chặn storage → coi như không phải app mobile. */ }

        const useAppLayout = shouldUseAppLayout({
            width: window.innerWidth,
            coarsePointer: !!window.matchMedia?.('(pointer: coarse)')?.matches,
            userAgent: navigator.userAgent,
            isMobileApp,
        });
        if (!useAppLayout) {
            return false;
        }

        url.searchParams.set('layoutUI', 'app');
        window.location.replace(url.toString());
        return true;
    }

    /**
     * Đổi phiên bản giao diện bằng full-page redirect để PHP nhận layoutUI từ request.
     */
    switchVersion(version: 'v1' | 'v2'): void {
        const targetUrl = this.getConversationUrl(version);

        if (isPlatformBrowser(this.platformId)) {
            window.location.assign(targetUrl);
            return;
        }

        void this.router.navigateByUrl(targetUrl);
    }

    /**
     * Giữ query params hiện tại (ví dụ cid) và ghi đè layoutUI.
     * V2 trên viewport mobile phải đi qua shell `app`, không dùng bản web V2
     * responsive để PHP trả đúng layout ngay từ request đầu tiên.
     */
    getConversationUrl(version: 'v1' | 'v2'): string {
        const targetUrl = this.router.parseUrl(CONVERSATION_PATHS[version]);
        targetUrl.queryParams = {
            ...this.router.parseUrl(this.router.url).queryParams,
            layoutUI: this.getLayoutUiRequestValue(version),
        };
        return this.router.serializeUrl(targetUrl);
    }

    private getLayoutUiRequestValue(version: 'v1' | 'v2'): 'v1' | 'v2' | 'app' {
        if (version === 'v2' && isPlatformBrowser(this.platformId) && window.innerWidth < APP_LAYOUT_MAX_WIDTH) {
            return 'app';
        }
        return version;
    }
}
